/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser"

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */

// Test Dummy Init Vals
// ====================
// Docker container build loads from config.js file dynamically written by entrypoint.sh
// Local dev server loads from config.js checked into repo
// Test runner does not load index.html, so config.js is never read
// This config is loaded during store setup, but without window._env_ set by index.html <script>
// These dummy values ensure the store can load without errors in test runner
const test_env_ = {
  AFFILIATE_CUSTOMER_TENANT_NAME: "fakeTenant",
  AFFILIATE_WEB_CLIENT_ID: "88888888-8888-8888-8888-888888888888",
  AFFILIATE_CUSTOMER_TENANT_DOMAIN_NAME: "string",
  AFFILIATE_API_BASE_ENDPOINT: "",
  CARECONNECT_CMS_ENDPOINT: "",
  ConnectionStrings__ApplicationInsights: "",
  AZURE_MAPS_SUBSCRIPTION_KEY: "",
  AZURE_MAPS_CLIENT_ID: "",
  MIGRATION_SERVICE_USER_ID: "",
  POSTHOG_API_ENDPOINT: "",
  POSTHOG_API_KEY: "",
  GOOGLE_ANALYTICS_MEASUREMENT_ID: "",
  EXPOSE_TEST_STORE: false,
  ALLOWED_SCORING_PROFILES: "[]",
  REFERRAL_EXPIRATION_DAYS: "10",
  ENABLE_MSW: false,
}

if (!window._env_) {
  window._env_ = test_env_
}

const tenantName = window._env_.AFFILIATE_CUSTOMER_TENANT_NAME
const baseUrl = `https://${tenantName}.b2clogin.com/${tenantName}.onmicrosoft.com`
const signUpSignIn = "B2C_1_SignUpSignIn"
const haSignUpSignIn = "B2C_1_HA_SignUpSignIn"
const totpSignUpSignIn = "B2C_1_TOTP_SignUpSignIn"
const userSignUp = "B2C_1_SignUp"

export const b2cPolicies = {
  names: {
    signUpSignIn,
    haSignUpSignIn,
    totpSignUpSignIn,
    userSignUp,
  },
  authorities: {
    signUpSignIn: {
      authority: `${baseUrl}/${signUpSignIn}`,
    },
    haSignUpSignIn: {
      authority: `${baseUrl}/${haSignUpSignIn}`,
    },
    totpSignUpSignIn: {
      authority: `${baseUrl}/${totpSignUpSignIn}`,
    },
    userSignUp: {
      authority: `${baseUrl}/${userSignUp}`,
    },
  },
  authorityDomain: `${tenantName}.b2clogin.com`,
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig = {
  auth: {
    authority: b2cPolicies.authorities.signUpSignIn.authority, // Default to Affiliate login flow
    clientId: window._env_.AFFILIATE_WEB_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
    redirectUri: "/", // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
    postLogoutRedirectUri: "/", // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  names: b2cPolicies.names,
  authorities: b2cPolicies.authorities,
  cache: {
    cacheLocation: "localStorage", // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level: any, message: any, containsPii: any) => {
        if (containsPii) {
          return
        }
        if (level <= msalConfig.system.loggerOptions.LogLevel) {
          switch (level) {
            case LogLevel.Error:
              console.error(message)
              break
            case LogLevel.Warning:
              console.warn(message)
              break
            case LogLevel.Info:
              console.info(message)
              break
            case LogLevel.Verbose:
              console.debug(message)
              break
            default:
              break
          }
        }
      },
      // Set this to LogLevel.Info for extra logs from MSAL as needed for debugging purposes
      LogLevel: LogLevel.Warning,
    },
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: [],
}

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
  scopes: ["openid", "profile"],
  loginHint: "example@domain.net",
}

export function GetAuthority(tenantId: string) {
  return baseUrl + "/" + tenantId
}
